// Authentication service for API calls
class AuthService {
  constructor() {
    // this.baseURL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3000/stockvel';
    this.baseURL = process.env.REACT_APP_API_BASE_URL || 'https://cfdapi.sharenet.co.za/stockvel';
  }

  buildUrl(path) {
    const base = this.baseURL.replace(/\/+$/, '');
    const suffix = path.replace(/^\/+/, '');
    return `${base}/${suffix}`;
  }

  // Login user with email and password
  async login(email, password) {
    try {
      const response = await fetch(this.buildUrl('/auth/login'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        return {
          success: false,
          error: data.error || data.message || 'Login failed. Please check your credentials.',
        };
      }

      const token = data.token || data.accessToken || data.jwt;
      if (!token) {
        return { success: false, error: 'Login failed. Missing token response.' };
      }

      const rawUser = data.user || data.account || data.member || data.data?.user || {};
      const normalizedUser = {
        id: rawUser.id || data.userId || data.id,
        name:
          rawUser.name ||
          data.name ||
          [rawUser.first_name, rawUser.last_name].filter(Boolean).join(' ').trim(),
        email: rawUser.email || data.email || email,
        phone_number: rawUser.phone_number || rawUser.phone || data.phone || '',
        user_group: rawUser.user_group || data.user_group || '',
        approved: rawUser.approved ?? data.approved ?? true,
        roles: rawUser.roles || data.roles || [],
        status: rawUser.status || data.status || '',
        reference_number: rawUser.reference_number || data.referenceNumber || null,
      };

      const expiryTime = data.expiresIn
        ? Date.now() + Number(data.expiresIn) * 1000
        : data.expiresAt
          ? new Date(data.expiresAt).getTime()
          : Date.now() + 60 * 60 * 1000;

      localStorage.setItem('authToken', token);
      localStorage.setItem('tokenExpiry', expiryTime.toString());
      localStorage.setItem('userData', JSON.stringify(normalizedUser));

      return {
        success: true,
        data: {
          token,
          user: normalizedUser,
        },
      };
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  // Register new user
  async register(userData) {
    try {
      const response = await fetch(this.buildUrl('/auth/register'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      const data = await response.json();
      return response.ok 
        ? { success: true, data }
        : { success: false, error: data.message || 'Registration failed' };
    } catch (error) {
      console.error('Registration error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  // Logout user
  logout() {
    localStorage.removeItem('authToken');
    localStorage.removeItem('tokenExpiry');
    localStorage.removeItem('userData');
  }

  // Get current token
  getToken() {
    const token = localStorage.getItem('authToken');
    const expiry = localStorage.getItem('tokenExpiry');
    
    if (token && expiry) {
      const now = new Date().getTime();
      if (now < parseInt(expiry)) {
        return token;
      } else {
        this.logout(); // Token expired
        return null;
      }
    }
    return null;
  }

  // Get current user data
  getCurrentUser() {
    const userData = localStorage.getItem('userData');
    const token = this.getToken();
    
    return token && userData ? JSON.parse(userData) : null;
  }

  // Check if user is authenticated
  isAuthenticated() {
    return this.getToken() !== null;
  }

  // Get authorization headers for API calls
  getAuthHeaders() {
    const token = this.getToken();
    return token ? { 'Authorization': `Bearer ${token}` } : {};
  }

  // Make authenticated API request
  async authenticatedRequest(url, options = {}) {
    const headers = {
      'Content-Type': 'application/json',
      ...this.getAuthHeaders(),
      ...options.headers,
    };

    try {
      const response = await fetch(this.buildUrl(url), {
        ...options,
        headers,
      });

      if (response.status === 401) {
        // Token expired or invalid
        this.logout();
        window.location.href = '/login';
        return null;
      }

      return response;
    } catch (error) {
      console.error('API request error:', error);
      throw error;
    }
  }

  // Refresh token (if backend supports it)
  async refreshToken() {
    const token = localStorage.getItem('authToken');
    if (!token) return false;

    try {
      const response = await fetch(this.buildUrl('/auth/refresh'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        const expiryTime = new Date().getTime() + (60 * 60 * 1000); // 1 hour from now
        
        localStorage.setItem('authToken', data.token);
        localStorage.setItem('tokenExpiry', expiryTime.toString());
        
        return true;
      } else {
        this.logout();
        return false;
      }
    } catch (error) {
      console.error('Token refresh error:', error);
      this.logout();
      return false;
    }
  }

  // Forgot password
  async forgotPassword(email) {
    try {
      const response = await fetch(this.buildUrl('/auth/forgot-password'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email }),
      });

      const data = await response.json();
      return response.ok 
        ? { success: true, data }
        : { success: false, error: data.message || 'Failed to send reset email' };
    } catch (error) {
      console.error('Forgot password error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async requestPasswordReset(email, phone = '', newAccount = false) {
    try {
      const response = await fetch(this.buildUrl('/auth/reset/request'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, phone, new_account: newAccount }),
      });

      const data = await response.json();
      return response.ok
        ? { success: true, data }
        : { success: false, error: data.error || 'Password reset failed' };
    } catch (error) {
      console.error('Reset request error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async verifyPasswordReset(email, code) {
    try {
      const response = await fetch(this.buildUrl('/auth/reset/verify'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, code }),
      });

      const data = await response.json();
      return response.ok
        ? { success: true, data }
        : { success: false, error: data.error || 'Verification failed' };
    } catch (error) {
      console.error('Reset verify error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  async confirmPasswordReset(email, newPassword) {
    try {
      const response = await fetch(this.buildUrl('/auth/reset/confirm'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, newPassword }),
      });

      const data = await response.json();
      return response.ok
        ? { success: true, data }
        : { success: false, error: data.error || 'Password reset failed' };
    } catch (error) {
      console.error('Reset confirm error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }

  // Reset password
  async resetPassword(token, newPassword) {
    try {
      const response = await fetch(`${this.baseURL}/auth/reset-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token, password: newPassword }),
      });

      const data = await response.json();
      return response.ok 
        ? { success: true, data }
        : { success: false, error: data.message || 'Failed to reset password' };
    } catch (error) {
      console.error('Reset password error:', error);
      return { success: false, error: 'Network error. Please try again.' };
    }
  }
}

export default new AuthService();
